// STOREFRONT_URL is only used for "open on storefront" / product preview links.
// The CMS API endpoints are routed through the admin's own nginx (which proxies
// /api/cms/ to the customer-web container) so cmsUrl returns a relative path
// and works correctly behind docker without needing a build-time origin.
// Falls back to the production storefront so preview links never resolve to the
// admin origin (which has no storefront routes) when the env var is unset.
export const STOREFRONT_URL =
  (import.meta.env.VITE_STOREFRONT_URL as string | undefined) || 'https://shishyastra.com';

export const cmsUrl = (slug: string) => `/api/cms/${slug}`;

/**
 * fetch() with a couple of automatic retries — survives the brief window where
 * nginx still holds the old customer-web container IP during a docker rebuild.
 * Retries on network errors and 502/503/504, then surfaces the final failure.
 */
export async function cmsFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  // CMS write endpoints verify this token against the API — without it,
  // PUT/POST are rejected with 401.
  const token = localStorage.getItem('admin_token');
  const headers = new Headers(init?.headers);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const authedInit: RequestInit = { ...init, headers };

  const attempts = 3;
  let lastErr: unknown = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(input, authedInit);
      if (res.ok || ![502, 503, 504].includes(res.status)) return res;
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (err) {
      lastErr = err;
    }
    if (i < attempts - 1) {
      // 250ms, 750ms backoff — total worst case ~1s before user-visible failure
      await new Promise((r) => setTimeout(r, 250 * (i + 1) ** 2));
    }
  }
  throw lastErr ?? new Error('cmsFetch: unknown error');
}
